import {
  isValidQuotePrice,
  NEGATIVE_CELL_CLASSNAME,
  NEGATIVE_CELL_DARK_CLASSNAME,
  NEGATIVE_CELL_DIM_CLASSNAME,
  POSITIVE_CELL_CLASSNAME,
  POSITIVE_CELL_DARK_CLASSNAME,
  POSITIVE_CELL_DIM_CLASSNAME,
  QuoteStatusEnum,
  SideEnum,
  SUBTLE_TEXT_CELL_CLASSNAME,
} from '@talos/kyoko';
import type { CellClassRules } from 'ag-grid-community';
import { isPricingRow, QuoteMarketRow, QuoteRow, type QuotesBlotterRow } from './types';

/**
 * Bid/offer coloring rules for Quotes Blotter V2.
 *
 * Definitions:
 * - Enabled side means the side the user can act on for the care order.
 *   For Buy care orders, Offer is enabled. For Sell care orders, Bid is enabled.
 *   If there is no care-order side, both sides are enabled.
 * - Highlightable quote means we have a real, usable price on that side:
 *   the side has a value, the market is Open, and the side is enabled.
 * - No-valid-response means a child market row does not have a valid executable
 *   price on that side (for example PendingNew placeholder values like 0).
 *
 * Rules:
 * - Highlightable parent bid/offer -> default highlight.
 * - Highlightable child best bid/offer -> dim highlight.
 * - Highlightable child non-best bid/offer -> dark highlight.
 * - Child non-highlightable priced side -> no background highlight.
 * - Child no-valid-response side -> subtle text always, plus dark background only when that side is enabled.
 *
 * Cypress coverage:
 * - apps/ava-cypress-components/cypress/component/RFQ/QuotesBlotterV2.cy.tsx
 *   ("applies bid/offer coloring classes for parent and child relevance scenarios")
 */

const isQuoteRow = (data: QuotesBlotterRow | undefined): data is QuoteRow => data instanceof QuoteRow;

const isQuoteMarketRow = (data: QuotesBlotterRow | undefined): data is QuoteMarketRow => data instanceof QuoteMarketRow;

const isBidSideEnabled = (data: QuoteRow | QuoteMarketRow | undefined): boolean =>
  data?.careOrderSide == null || data.careOrderSide === SideEnum.Sell;

const isOfferSideEnabled = (data: QuoteRow | QuoteMarketRow | undefined): boolean =>
  data?.careOrderSide == null || data.careOrderSide === SideEnum.Buy;

/** Returns true when the bid column should be highlighted (price exists, quote is open, and care order side is not opposite). */
const isBidHighlightable = (data: QuoteRow | QuoteMarketRow | undefined): boolean => {
  if (data?.bid?.BidPx == null || data.bid.QuoteStatus !== QuoteStatusEnum.Open) {
    return false;
  }
  return isBidSideEnabled(data);
};

/** Returns true when the offer column should be highlighted (price exists, quote is open, and care order side is not opposite). */
const isOfferHighlightable = (data: QuoteRow | QuoteMarketRow | undefined): boolean => {
  if (data?.offer?.OfferPx == null || data.offer.QuoteStatus !== QuoteStatusEnum.Open) {
    return false;
  }
  return isOfferSideEnabled(data);
};

const hasNoValidBidResponse = (data: QuoteRow | QuoteMarketRow | undefined): boolean =>
  !!data && isQuoteMarketRow(data) && !isValidQuotePrice(data.bid?.BidPx, data.bid?.QuoteStatus, data.PricingMode);

const hasNoValidOfferResponse = (data: QuoteRow | QuoteMarketRow | undefined): boolean =>
  !!data &&
  isQuoteMarketRow(data) &&
  !isValidQuotePrice(data.offer?.OfferPx, data.offer?.QuoteStatus, data.PricingMode);

const isBestBid = (data: QuoteRow | QuoteMarketRow | undefined) => !!data?.bid?.isBestBid;

const isBestOffer = (data: QuoteRow | QuoteMarketRow | undefined) => !!data?.offer?.isBestOffer;

interface BuildPriceCellClassRulesParams {
  defaultClassName: string;
  dimClassName: string;
  darkClassName: string;
  isHighlightable: (data: QuoteRow | QuoteMarketRow | undefined) => boolean;
  isBest: (data: QuoteRow | QuoteMarketRow | undefined) => boolean;
  isSideEnabled: (data: QuoteRow | QuoteMarketRow | undefined) => boolean;
  hasNoValidResponse: (data: QuoteRow | QuoteMarketRow | undefined) => boolean;
}

const buildPriceCellClassRules = ({
  defaultClassName,
  dimClassName,
  darkClassName,
  isHighlightable,
  isBest,
  isSideEnabled,
  hasNoValidResponse,
}: BuildPriceCellClassRulesParams): CellClassRules<QuotesBlotterRow> => ({
  [defaultClassName]: params => isPricingRow(params.data) && isQuoteRow(params.data) && isHighlightable(params.data),
  [dimClassName]: params =>
    isPricingRow(params.data) && isQuoteMarketRow(params.data) && isHighlightable(params.data) && isBest(params.data),
  [darkClassName]: params =>
    isPricingRow(params.data) &&
    isQuoteMarketRow(params.data) &&
    ((hasNoValidResponse(params.data) && isSideEnabled(params.data)) ||
      (isHighlightable(params.data) && !isBest(params.data))),
  [SUBTLE_TEXT_CELL_CLASSNAME]: params => isPricingRow(params.data) && hasNoValidResponse(params.data),
});

export const bidCellClassRules: CellClassRules<QuotesBlotterRow> = buildPriceCellClassRules({
  defaultClassName: NEGATIVE_CELL_CLASSNAME,
  dimClassName: NEGATIVE_CELL_DIM_CLASSNAME,
  darkClassName: NEGATIVE_CELL_DARK_CLASSNAME,
  isHighlightable: isBidHighlightable,
  isBest: isBestBid,
  isSideEnabled: isBidSideEnabled,
  hasNoValidResponse: hasNoValidBidResponse,
});

export const offerCellClassRules: CellClassRules<QuotesBlotterRow> = buildPriceCellClassRules({
  defaultClassName: POSITIVE_CELL_CLASSNAME,
  dimClassName: POSITIVE_CELL_DIM_CLASSNAME,
  darkClassName: POSITIVE_CELL_DARK_CLASSNAME,
  isHighlightable: isOfferHighlightable,
  isBest: isBestOffer,
  isSideEnabled: isOfferSideEnabled,
  hasNoValidResponse: hasNoValidOfferResponse,
});
